export interface StatBucket { report_count: number; transaction_count: number; total_cad: number; }

export interface DashboardSummary {
  statistics: {
    accepted: { count: number; total_cad: number };
    submitted_to_fintrac: { report_count: number; transaction_count: number; total_cad: number;
      by_status: Record<string, StatBucket> };
    pending: { report_count: number; transaction_count: number; total_cad: number };
    /** FINTRAC's own verdict, counted server-side. Not derivable from
     *  `by_status`: a filed report's candidate ends SUBMITTED (never
     *  ACCEPTED), and a rejected one ends ERROR like our own failures. */
    filed_with_fintrac: { report_count: number; by_report_type: Record<string, number> };
    /** Filed, then deleted at FINTRAC. Optional: older API deployments omit it. */
    withdrawn?: { report_count: number; transaction_count: number; total_cad: number };
  };
  by_day: Array<{
    date: string;
    accepted: { count: number; total_cad: number };
    submitted: { report_count: number; transaction_count: number; total_cad: number };
  }>;
}

/** GET /portal/rejections/summary. */
export interface RejectionsSummary {
  fintrac: {
    report_count: number;
    next_deadline: string | null;
    overdue_count: number;
    groups: Array<{
      /** null: FINTRAC refused the report without a recognisable rule code. */
      rule: string | null;
      report_count: number;
      report_types: string[];
      next_deadline: string | null;
      overdue_count: number;
    }>;
  };
  /** Reports that failed inside our pipeline. `groups` is internal-only. */
  pipeline: {
    report_count: number;
    next_deadline: string | null;
    overdue_count: number;
    groups?: Array<{ signature: string; report_count: number }>;
  };
  validation: {
    api: { row_count: number; groups: Array<{ field: string | null; code: string | null; count: number }> };
    sftp: { row_count: number; file_count: number; groups: Array<{ code: string; count: number }> };
  };
  truncated: boolean;
}

export interface DashboardCards {
  transactionsAccepted: number;
  filedByType: Record<string, number>;
  filedTotal: number;
  withdrawnTotal: number;
}

export interface RejectionCards {
  fintracRejected: number;
  pipelineErrors: number;
  rejectedRows: number;
}

export function computeDashboardCards(summary: DashboardSummary): DashboardCards {
  // Optional-chained: an API not yet redeployed with filed_with_fintrac must
  // read as 0, not crash the dashboard.
  const filed = summary.statistics.filed_with_fintrac;
  return {
    transactionsAccepted: summary.statistics.accepted.count,
    filedByType: filed?.by_report_type ?? {},
    filedTotal: filed?.report_count ?? 0,
    withdrawnTotal: summary.statistics.withdrawn?.report_count ?? 0,
  };
}

/** True when `r` has the fields the dashboard and summary page read. This
 *  app has no ErrorBoundary, so an unexpected API shape that a render reads
 *  unchecked unmounts the whole page; callers treat a mismatch as "no data". */
export function isRejectionsSummary(r: unknown): r is RejectionsSummary {
  const x = r as RejectionsSummary | null | undefined;
  return Boolean(x?.fintrac?.groups && x.pipeline && x.validation?.api?.groups
    && x.validation?.sftp?.groups);
}

/** From the rejections summary rather than the dashboard summary, so each
 *  card's number is exactly what clicking through to the Rejections page
 *  shows -- the two endpoints window FINTRAC failures differently. */
export function computeRejectionCards(r: RejectionsSummary): RejectionCards {
  return {
    fintracRejected: r.fintrac.report_count,
    pipelineErrors: r.pipeline.report_count,
    rejectedRows: r.validation.api.row_count + r.validation.sftp.row_count,
  };
}

/** The dashboard banner's sentence, or null when there is nothing unfiled.
 *  Only FINTRAC rejections raise it: an unfiled report has a regulatory
 *  deadline running, which nothing else on the page does. */
export function fintracBannerText(r: RejectionsSummary): string | null {
  const f = r.fintrac;
  if (f.report_count === 0) return null;
  const reports = `${f.report_count} report${f.report_count === 1 ? "" : "s"} rejected by FINTRAC and not filed`;
  const parts = [reports];
  if (f.next_deadline) parts.push(`nearest deadline ${f.next_deadline}`);
  if (f.overdue_count > 0) parts.push(`${f.overdue_count} past deadline`);
  return parts.join(" — ");
}
